<template>
  <div class="mod-vip">
    <div class="page-header">
      <span class="page-header__title">会员管理</span>
    </div>

    <div class="page-body">
      <!-- 会员统计 -->
      <div class="panel">
        <div class="panel__header">当前活跃会员统计</div>
        <div class="panel__body">
          <el-row :gutter="20" v-loading="statLoading">
            <el-col :xs="24" :sm="12" :lg="6" v-for="item in statCards" :key="item.label">
              <div class="stat-card">
                <div class="stat-card__label">{{ item.label }}</div>
                <div class="stat-card__value">{{ formatNumber(item.value) }}</div>
              </div>
            </el-col>
          </el-row>
        </div>
      </div>

      <!-- 会员权益记录 -->
      <div class="panel">
        <div class="panel__header">
          <span>会员权益记录</span>
          <div class="panel__header-extra">
            <el-input
              v-model="recordForm.memberCode"
              placeholder="用户号"
              clearable
              size="mini"
              class="filter__input"
              @keyup.enter.native="handleRecordSearch">
            </el-input>
            <el-select
              v-model="recordForm.vipLevel"
              placeholder="全部等级"
              clearable
              size="mini"
              class="filter__select"
              @change="handleRecordSearch">
              <el-option
                v-for="item in vipLevelOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
            <el-select
              v-model="recordForm.status"
              placeholder="全部状态"
              clearable
              size="mini"
              class="filter__select"
              @change="handleRecordSearch">
              <el-option
                v-for="(label, value) in statusMap"
                :key="value"
                :label="label"
                :value="value">
              </el-option>
            </el-select>
            <el-button type="primary" size="mini" icon="el-icon-search" @click="handleRecordSearch">查询</el-button>
          </div>
        </div>
        <div class="panel__body panel__body--table">
          <el-table :data="recordList" v-loading="recordLoading" style="width: 100%;">
            <el-table-column prop="memberCode" label="用户号" min-width="140"></el-table-column>
            <el-table-column label="账号" min-width="140">
              <template slot-scope="scope">{{ scope.row.memberName || '—' }}</template>
            </el-table-column>
            <el-table-column label="会员类型" min-width="110">
              <template slot-scope="scope">{{ vipLevelLabel(scope.row.vipLevel) }}</template>
            </el-table-column>
            <el-table-column label="开通方式" min-width="120">
              <template slot-scope="scope">{{ sourceLabel(scope.row.sourceType) }}</template>
            </el-table-column>
            <el-table-column prop="startTime" label="开始时间" min-width="160">
              <template slot-scope="scope">{{ scope.row.startTime || '—' }}</template>
            </el-table-column>
            <el-table-column label="到期时间" min-width="160">
              <template slot-scope="scope">{{ endTimeLabel(scope.row) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="120">
              <template slot-scope="scope">
                <el-tag :type="statusTagType(scope.row)" size="small">{{ statusLabel(scope.row) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="orderId" label="关联订单" min-width="140">
              <template slot-scope="scope">{{ scope.row.orderId || '—' }}</template>
            </el-table-column>
          </el-table>
          <el-pagination
            :current-page="page"
            :page-sizes="[10, 20, 50]"
            :page-size="limit"
            :total="total"
            layout="total, sizes, prev, pager, next, jumper"
            class="pagination"
            @size-change="handleSizeChange"
            @current-change="handlePageChange">
          </el-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getUserPage,
  getVipSubscriptionPage,
  VIP_LEVEL_MAP,
  VIP_LEVEL_OPTIONS,
  SOURCE_TYPE_MAP,
  SUBSCRIPTION_STATUS_MAP,
  VIP_STAT_GROUPS,
  VIP_PERIOD_FOREVER
} from '@/api/userCenter'

// 距到期多少天内算「即将到期」
const EXPIRING_DAYS = 7
// 订阅状态：生效中
const STATUS_ACTIVE = 1

export default {
  name: 'VipManage',
  data () {
    return {
      statLoading: false,
      recordLoading: false,
      statCards: VIP_STAT_GROUPS.map(group => ({ label: group.label, value: 0 })),
      recordList: [],
      page: 1,
      limit: 10,
      total: 0,
      recordForm: {
        memberCode: '',
        vipLevel: '',
        status: ''
      }
    }
  },

  computed: {
    vipLevelOptions () {
      return VIP_LEVEL_OPTIONS
    },
    statusMap () {
      return SUBSCRIPTION_STATUS_MAP
    }
  },

  created () {
    this.loadStat()
    this.loadRecords()
  },

  methods: {
    // ---------- 会员统计 ----------
    async loadStat () {
      this.statLoading = true
      const safe = promise => promise.catch(e => {
        console.error('加载会员统计失败:', e)
        return null
      })

      // 每个分组可能对应多个 vipPeriod，分别查总数后相加
      const tasks = VIP_STAT_GROUPS.map(group =>
        Promise.all(group.periods.map(period =>
          safe(getUserPage({ page: 1, limit: 1, memberVip: 1, vipPeriod: period }))
        ))
      )

      const results = await Promise.all(tasks)
      this.statCards = VIP_STAT_GROUPS.map((group, index) => ({
        label: group.label,
        value: results[index].reduce((sum, res) => sum + this.pickTotal(res), 0)
      }))
      this.statLoading = false
    },

    pickTotal (res) {
      const body = res && res.data
      if (!body || body.code !== 0 || !body.data) return 0
      return body.data.total || 0
    },

    // ---------- 会员权益记录 ----------
    async loadRecords () {
      this.recordLoading = true
      const params = {
        page: this.page,
        limit: this.limit
      }
      if (this.recordForm.memberCode) {
        params.memberCode = this.recordForm.memberCode
      }
      if (this.recordForm.vipLevel !== '') {
        params.vipLevel = this.recordForm.vipLevel
      }
      if (this.recordForm.status !== '') {
        params.status = this.recordForm.status
      }

      try {
        const { data: res } = await getVipSubscriptionPage(params)
        if (res.code !== 0) {
          this.recordList = []
          this.total = 0
          this.$message.error(res.msg)
          return
        }
        const list = (res.data && res.data.list) || []
        this.recordList = list.map(this.normalizeRecord)
        this.total = (res.data && res.data.total) || 0
      } catch (e) {
        console.error('加载会员权益记录失败:', e)
        this.$message.error('加载会员权益记录失败')
      } finally {
        this.recordLoading = false
      }
    },

    // 该后端两种字段命名风格并存（公告接口是下划线），两种都兼容一下
    normalizeRecord (item) {
      const pick = (camel, snake) => (item[camel] !== undefined && item[camel] !== null ? item[camel] : item[snake])
      return {
        ...item,
        memberCode: pick('memberCode', 'member_code') || '',
        memberName: pick('memberName', 'member_name') || '',
        vipLevel: pick('vipLevel', 'vip_level'),
        startTime: pick('startTime', 'start_time') || '',
        endTime: pick('endTime', 'end_time') || '',
        sourceType: pick('sourceType', 'source_type'),
        orderId: pick('orderId', 'order_id') || ''
      }
    },

    handleRecordSearch () {
      this.page = 1
      this.loadRecords()
    },

    handleSizeChange (val) {
      this.limit = val
      this.page = 1
      this.loadRecords()
    },

    handlePageChange (val) {
      this.page = val
      this.loadRecords()
    },

    // ---------- 展示 ----------
    vipLevelLabel (level) {
      return VIP_LEVEL_MAP[level] || '—'
    },

    sourceLabel (sourceType) {
      return SOURCE_TYPE_MAP[sourceType] || '—'
    },

    endTimeLabel (row) {
      if (row.vipLevel === VIP_PERIOD_FOREVER) {
        return '永久'
      }
      return row.endTime || '—'
    },

    // 生效中且临近到期的，单独标记为「即将到期」
    isExpiring (row) {
      if (row.status !== STATUS_ACTIVE || !row.endTime || row.vipLevel === VIP_PERIOD_FOREVER) {
        return false
      }
      const end = new Date(String(row.endTime).replace(/-/g, '/')).getTime()
      if (Number.isNaN(end)) {
        return false
      }
      const diff = end - new Date().getTime()
      return diff > 0 && diff <= EXPIRING_DAYS * 24 * 3600 * 1000
    },

    statusLabel (row) {
      if (this.isExpiring(row)) {
        return '即将到期'
      }
      return SUBSCRIPTION_STATUS_MAP[row.status] || '未知'
    },

    statusTagType (row) {
      if (this.isExpiring(row)) {
        return 'warning'
      }
      const typeMap = {
        0: 'info',
        1: 'success',
        2: 'info',
        3: 'info',
        4: 'danger'
      }
      return typeMap[row.status] || 'info'
    },

    formatNumber (value) {
      return String(Number(value) || 0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style lang="scss" scoped>
.mod-vip {
  background-color: #f5f7fa;
  min-height: 100%;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: #fff;
  border-bottom: 1px solid #ebeef5;

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2d3d;
  }
}

.page-body {
  padding: 16px;
}

.panel {
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  margin-bottom: 20px;

  &__header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
    font-size: 14px;
    font-weight: 600;
    color: #1f2d3d;
  }

  &__tip {
    margin-left: 10px;
    font-size: 12px;
    font-weight: 400;
    color: #909399;
  }

  &__header-extra {
    display: flex;
    align-items: center;
    margin-left: auto;

    > * {
      margin-left: 10px;
    }
  }

  &__body {
    padding: 16px;

    &--table {
      padding: 0 16px 16px;
    }
  }
}

.stat-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 10px;

  &__label {
    font-size: 14px;
    color: #909399;
  }

  &__value {
    font-size: 24px;
    font-weight: 600;
    color: #1f2d3d;
    line-height: 1.4;
    margin-top: 8px;
  }
}

.filter {
  &__input {
    width: 140px;
  }

  &__select {
    width: 120px;
  }
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

::v-deep .el-table th {
  background-color: #fafbfc;
  color: #909399;
  font-weight: 500;
}
</style>
